<template>
  <div>
    <px-view-header title="Edição de cotação"
                    title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1"
               color="primary"
               icon
               x-large
               @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>
    <v-card class="rounded-card">
      <v-card-text>
        <div v-if="loadingCotacao">
          <px-circular-loading/>
        </div>
        <div v-if="cotacao" class="text--primary">
          <v-row dense>
            <v-col cols="12">
              <v-subheader class="body-1 grey lighten-4 text--primary">
                <v-icon>
                  mdi-text-box-outline
                </v-icon>
                <span class="ml-2">Dados gerais</span>
              </v-subheader>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="d-flex" cols="12" sm="12" md="6">
              <px-select-enum dense
                              label="Tipo de frete"
                              class="rounded-tag-small"
                              :items="tiposFretes"
                              v-model="cotacao.tipoFrete"/>
            </v-col>
            <v-col class="d-flex" cols="12" sm="12" md="6">
              <px-date-picker dense
                              label="Prazo de entrega solicitado"
                              input-class="rounded-tag-small"
                              v-model="cotacao.prazoEntrega"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="d-flex" cols="12" sm="12" md="6">
              <px-date-picker dense
                              label="Prazo de resposta"
                              input-class="rounded-tag-small"
                              v-model="cotacao.validade"/>
            </v-col>
            <v-col class="d-flex" cols="12" sm="12" md="6">
              <px-select-enum dense
                              :items="formasPagamentos"
                              class="rounded-tag-small"
                              label="Forma pagamento"
                              v-model="cotacao.formaPagamento"
                              @change="onChangeFormaPagamento"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="d-flex" cols="12">
              <px-autocomplete-prazo-pagamento dense
                                               class="rounded-tag-small"
                                               v-model="cotacao.prazoPagamento"
                                               :disabled="isAVista"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-textarea dense
                          outlined
                          rows="2"
                          v-model="cotacao.observacao"
                          name="input-7-4"
                          label="Observações"
                          counter="1500"
                          class="rounded-tag-small"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-subheader class="body-1 grey lighten-4 text--primary">
                <v-icon>
                  mdi-package-variant
                </v-icon>
                <span class="ml-2">Produtos</span>
              </v-subheader>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-simple-table
                class="ma-4 produtos-table">
                <template v-slot:default>
                  <tbody>
                  <tr v-for="(produto) in cotacao.produtos"
                      :key="produto.id"
                      style="height: 60px"
                      class="no-hover">
                      <td class="px-1" style="min-width: 280px">
                        <px-span-tooltip v-if="checkCodigo(produto.produto)"
                                    top
                                    :text="labelCodigo(produto.produto)"
                                    tooltip="Código interno"/>
                      {{ produto.produto.descricao }}
                    ({{ produto.quantidade
                      | number('0.00') }} {{ produto.unidadeMedida.descricao }})
                      </td>
                      <td class="px-1" style="min-width: 380px">
                        <v-textarea
                          outlined
                          auto-grow
                          dense
                          hide-details
                          v-model="produto.especificacaoProduto"
                          label="Especificação"
                          counter="1500"
                          rows="1"
                          class="rounded-tag-small"/>
                      </td>
                      <td class="px-1" style="min-width: 20px">
                        <px-cadastro-produto-observacoes is-edicao
                                                         :produto="produto"/>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-subheader class="body-1 grey lighten-4 text--primary">
                <v-icon>
                  mdi-cart-outline
                </v-icon>
                <span class="ml-2">Finalidade da compra</span>
              </v-subheader>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-radio-group row
                             dense
                             class="d-flex ml-2"
                             v-model="cotacao.finalidadeCompra">
                <v-radio
                  v-for="finalidade in finalidades"
                  :key="finalidade.key"
                  :label="finalidade.description"
                  :value="finalidade.key"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <v-card-actions v-if="!loadingCotacao">
        <v-btn color="primary"
               class="normal-btn-text rounded-tag-small elevation-0 mb-2 ml-2"
               :loading="loading"
               @click="onClickSave">
          Salvar
        </v-btn>
        <v-btn text class="normal-btn-text mb-2" @click="onClickCancel">
          Cancelar
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import CotacaoService from '@/modules/cotacao/cotacao-service';
import {
  FormaPagamento, SituacaoCotacao, TipoFrete, FinalidadeCompra,
} from 'px-business-components';
import { deepNotEquals } from '@/utils/object-utils';

export default {
  data() {
    return {
      idCotacao: this.$route.params.id,
      loadingCotacao: false,
      loading: false,
      cotacao: null,
      initialCotacao: null,
      finalidades: FinalidadeCompra.values,
      tiposFretes: TipoFrete.values,
      formasPagamentos: FormaPagamento.values,
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Cotações',
          to: '/cotacoes',
        },
        { text: 'Editar cotação' },
      ],
    };
  },
  computed: {
    isAVista() {
      return this.cotacao.formaPagamento !== FormaPagamento.keys.A_PRAZO.key;
    },
    checkCodigo() {
      return item => item.codigo;
    },
    labelCodigo() {
      return item => `${item.codigo} - `;
    },
    isAberta() {
      return item => item.situacao === SituacaoCotacao.keys.ABERTA.key
        || item.situacao === SituacaoCotacao.keys.EM_ANDAMENTO.key;
    },
    produtosToEdit() {
      return this.cotacao.produtos.filter(prod => this.isAberta(prod));
    },
    isFormDirt() {
      return Object.keys(this.initialCotacao)
        .some(key => deepNotEquals(this.cotacao, this.initialCotacao, key));
    },
  },
  methods: {
    onChangeFormaPagamento() {
      this.cotacao.prazoPagamento = null;
    },
    beforeWindowUnload(e) {
      if (this.isFormDirt) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    onClickBack() {
      this.$router.push({ name: 'main.cotacoes.respostas', props: { id: this.cotacao.id } });
    },
    getCotacao() {
      this.loadingCotacao = true;
      CotacaoService.getCotacao(this.idCotacao)
        .then((response) => {
          this.cotacao = response.data;
          this.initialCotacao = cloneDeep(this.cotacao);
        })
        .finally(() => {
          this.loadingCotacao = false;
        });
    },
    onClickCancel() {
      this.$router.push({ name: 'main.cotacoes.respostas', props: { id: this.cotacao.id } });
    },
    save() {
      this.loading = true;
      CotacaoService.updateCotacao(this.cotacao)
        .then(() => {
          this.$toast('Cotação atualizada com sucesso', { color: 'success' });
          this.onClickCancel();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onClickSave() {
      this.save();
    },
  },
  created() {
    this.getCotacao();
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
};
</script>
